/*
 * Copyright (c) 2025 BuyerView.ai
 * All rights reserved.
 * Unauthorized copying prohibited.
 */

import { supabase } from './supabase';

// Custom error class for service-related errors
class ServiceError extends Error {
  constructor(message: string, public code: string) {
    super(message);
    this.name = 'ServiceError';
  }
}

const AVATAR_BUCKET = 'avatars';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export async function setupStorage() {
  try {
    // Test basic connectivity first
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/`, {
        method: 'HEAD',
        headers: {
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        }
      });
      
      if (!response.ok && response.status !== 404) {
        console.warn('Unable to connect to Supabase, continuing without storage features');
        return false;
      }
    } catch (fetchError) {
      if (fetchError instanceof ServiceError || 
          (fetchError instanceof TypeError && fetchError.message.includes('Failed to fetch')) ||
          (fetchError instanceof Error && fetchError.name === 'AbortError')) {
        console.warn('Storage setup skipped: Unable to connect to Supabase. Please check your internet connection.');
        return false;
      }
      console.warn('Storage setup failed:', fetchError);
      return false;
    }

    // Check if storage is enabled
    const { data: buckets, error: listError } = await supabase.storage.listBuckets();
    
    if (listError) {
      if (listError.message?.includes('Failed to fetch')) {
        console.warn('Storage setup skipped: Unable to connect to Supabase storage. Please check your internet connection.');
        return false;
      }
      if (listError.message?.includes('storage not enabled')) {
        console.warn('Storage is not enabled for this project');
        return false;
      }
      console.warn('Storage setup failed:', listError);
      return false;
    }

    const bucketExists = buckets?.some(bucket => bucket.name === AVATAR_BUCKET);
    
    if (!bucketExists) {      
      console.warn(
        'Avatars bucket not found. Please create it manually in the Supabase dashboard with public access enabled, ' +
        `a file size limit of ${MAX_FILE_SIZE / (1024 * 1024)}MB, and allowed MIME types: ${ALLOWED_MIME_TYPES.join(', ')}. ` +
        'Avatar upload features will be disabled until the bucket is created.'
      );
      return false;
    }

    // Validate the setup
    const isValid = await validateStorageSetup();
    if (!isValid) {
      console.warn(
        'Storage setup validation failed. Please ensure the "avatars" bucket exists in your Supabase project. ' +
        'Avatar upload features will be disabled.'
      );
      return false;
    }
    return isValid;
  } catch (error) {
    if (error instanceof ServiceError || 
        (error instanceof Error && error.name === 'AbortError')) {
      console.warn('Network issue during storage setup:', error.message);
    } else {
      console.warn('Storage setup failed, continuing without storage features:', error);
    }
    return false;
  }
}

export async function validateStorageSetup(): Promise<boolean> {
  try {
    // Check if bucket exists and is accessible
    const { data: bucket, error: bucketError } = await supabase.storage.getBucket(AVATAR_BUCKET);
    
    if (bucketError) {
      if (bucketError.message?.includes('storage not enabled')) {
        console.warn('Storage is not enabled for this project');
        return false;
      }
      
      if (bucketError.message?.includes('Bucket not found')) {
        console.warn('Avatars bucket does not exist');
        return false;
      }
      
      console.warn('Error accessing avatars bucket:', bucketError);
      return false;
    }

    // Verify bucket configuration
    if (!bucket.public) {
      console.warn('Avatars bucket must be public');
      return false;
    }

    // Test read access by listing files
    const { error: listError } = await supabase.storage
      .from(AVATAR_BUCKET)
      .list('', {
        limit: 1,
        offset: 0
      });

    if (listError) {
      if (listError.message?.includes('Bucket not found')) {
        console.warn('Avatars bucket does not exist');
        return false;
      }
      
      if (!listError.message?.includes('not found')) {
        console.warn('Error accessing bucket contents:', listError);
        return false;
      }
    }

    return true;
  } catch (error) {
    if (error instanceof Error) {
      if (error.message.includes('Failed to fetch')) {
        console.warn('Network error during storage validation - check Supabase connectivity');
        return false;
      }
      if (error.message.includes('Bucket not found')) {
        console.warn('Avatars bucket does not exist');
        return false;
      }
    }
    console.warn('Unexpected error during storage validation:', error);
    return false;
  }
}

export function getAvatarUrl(path: string): string | null {
  try {
    // First check if storage is properly set up
    const { data } = supabase.storage
      .from(AVATAR_BUCKET)
      .getPublicUrl(path);
    
    return data.publicUrl;
  } catch (error) {
    console.warn('Error getting avatar URL:', error);
    return null;
  }
}

export async function uploadAvatar(
  userId: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> {
  if (!userId) throw new Error('User ID is required');
  if (!file) throw new Error('File is required');

  // Validate file size
  if (file.size > MAX_FILE_SIZE) {
    throw new Error('Avatar file size must be less than 5MB');
  }

  // Validate file type
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    throw new Error('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
  }

  try {
    // Check storage setup first
    const isValid = await validateStorageSetup();
    if (!isValid) {
      throw new Error(
        'Storage is not properly configured. Please ensure storage is enabled in your Supabase project ' +
        'and the avatars bucket is set up correctly with public access.'
      );
    }

    // Generate unique file name
    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `${userId}/${crypto.randomUUID()}.${fileExt}`;

    // Upload file
    const { error: uploadError } = await supabase.storage
      .from(AVATAR_BUCKET)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
        onUploadProgress: ({ total, loaded }) => {
          onProgress?.(total ? (loaded / total) * 100 : 0);
        }
      });

    if (uploadError) {
      if (uploadError.message?.includes('storage not enabled')) {
        throw new Error('Storage is not enabled in your Supabase project');
      }
      if (uploadError.message?.includes('Bucket not found')) {
        throw new Error(
          'The avatars bucket does not exist. Please create it in the Supabase dashboard ' +
          'with public access enabled.'
        );
      }
      throw uploadError;
    }

    // Get public URL
    const publicUrl = getAvatarUrl(fileName);
    if (!publicUrl) {
      throw new Error('Failed to get public URL for uploaded avatar');
    }
    
    return publicUrl;
  } catch (error) {
    if (error instanceof Error) {
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Network error during upload. Please check your connection and try again.');
      }
      if (error.message.includes('Bucket not found')) {
        throw new Error(
          'The avatars bucket does not exist. Please create it in the Supabase dashboard ' +
          'with public access enabled.'
        );
      }
      throw error;
    }
    console.error('Error uploading avatar:', error);
    throw new Error('Failed to upload avatar. Please try again.');
  }
}